import React from "react";
import { Box, Paper, Typography, Tooltip } from "@mui/material";
import { DataGridPro, GridColDef, GridRowParams } from "@mui/x-data-grid-pro";
import { DragDropContext } from "react-beautiful-dnd";
import { KeyboardArrowRight, KeyboardArrowDown } from "@mui/icons-material";
import { Icon } from "@iconify/react";
import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

// Components
import Column from "./Column";
import { AutoScrollContainer } from "../components/AutoScrollContainer";

// Redux
import { useAppSelector } from "@/app/hooks";
import {
  selectBoardState,
  selectFilters,
  selectGroupingConfig,
  selectApiData,
  selectGroupingDataMemoized,
  selectSubGroupingDataMemoized,
} from "@/features/bughurd/boardSelector";
import { useDragAndDrop } from "./custom-hooks/useDragAndDrop";

interface SubGroupDataGridProps {
  handleTaskUpdate: (
    taskId: string,
    updatedTask: any | null,
    newColumnId?: string,
  ) => void;
  refreshColumnData: (columnId?: string, subGroupId?: string) => void;
  handleLoadMore: (columnId: string, subGroupId?: string) => void;
  isMyTaskView: boolean;
}

const SubGroupDataGrid: React.FC<SubGroupDataGridProps> = ({
  handleTaskUpdate,
  refreshColumnData,
  handleLoadMore,
  isMyTaskView,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();

  // Redux selectors
  const boardState = useAppSelector(selectBoardState);
  const filters = useAppSelector(selectFilters);
  const groupingConfig = useAppSelector(selectGroupingConfig);
  const apiData = useAppSelector(selectApiData);
  const groupingData = useAppSelector(selectGroupingDataMemoized);
  const subGroupingData = useAppSelector(selectSubGroupingDataMemoized);

  // Destructure from Redux state
  const { subGroupData, loadingStatuses } = boardState;

  const { groupBy, subGroupBy } = groupingConfig;
  const { currentUser } = useAppSelector((state) => state.appGlobal);
  const { handleDragStart, handleDragEnd } = useDragAndDrop();

  const getSubGroupGridColumns = (): GridColDef[] => {
    const orderedGroupIds = Object.keys(groupingData);
    const baseColumns: GridColDef[] = [];

    // Helper function to get display count
    const getDisplayCount = (groupId: string): string => {
      let totalCount = 0;
      let hasAnyLoading = false;

      Object.keys(subGroupData).forEach((subGroupId) => {
        const column = subGroupData[subGroupId]?.columns[groupId];
        if (column) {
          totalCount += column.totalCount || 0;
        }

        // Check if any sub-group is loading for this column
        const isLoading =
          subGroupData[subGroupId]?.loadingStatuses?.[groupId]
            ?.isInitialLoading;
        if (isLoading) {
          hasAnyLoading = true;
        }
      });

      if (hasAnyLoading) {
        return "...";
      }

      if (totalCount > 0) {
        return `${totalCount}`;
      }

      return totalCount.toString();
    };

    // First, add the sub-group label column
    baseColumns.push({
      field: "title",
      headerName: t(subGroupBy),
      width: 340,
      minWidth: 340,
      maxWidth: 340,
      flex: 0,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            px: 1,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              backgroundColor: theme.palette.background.paper,
              p: 1,
              borderRadius: "28px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary",
              height: 48,
              width: 340,
              minWidth: 340,
              maxWidth: 340,
              boxSizing: "border-box",
              position: "relative",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                color: theme.palette.text.primary,
              }}
            >
              {t(subGroupBy)}
            </Typography>
          </Paper>
        </Box>
      ),
      renderCell: (params) => {
        // Calculate total count for this sub-group across all columns
        const subGroupId = params.row.id;
        const getTotalCountForSubGroup = (): string => {
          let totalCount = 0;
          let hasAnyLoading = false;

          Object.keys(groupingData).forEach((groupId) => {
            const column = subGroupData[subGroupId]?.columns[groupId];
            if (column) {
              totalCount += column.totalCount || 0;
            }

            // Check if any column is loading for this sub-group
            const isLoading =
              subGroupData[subGroupId]?.loadingStatuses?.[groupId]
                ?.isInitialLoading;
            if (isLoading) {
              hasAnyLoading = true;
            }
          });

          if (hasAnyLoading) {
            return "...";
          }

          return totalCount.toString();
        };

        // Show sub-group info (icon + title + total count)
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
              px: 1,
            }}
          >
            {/* Sub-group info with total count */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {params.row.icon && (
                <Box
                  sx={{
                    bgcolor:
                      subGroupBy === "Status"
                        ? params.row.color
                        : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    p: 0.2,
                  }}
                >
                  <Icon
                    icon={params.row.icon}
                    width={20}
                    height={20}
                    style={{
                      color:
                        subGroupBy === "Status"
                          ? "text.secondary"
                          : params.row.color || "#666",
                    }}
                  />
                </Box>
              )}

              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {params.row.title || "Untitled"}
              </Typography>

              {/* Total count badge */}
              <Typography variant="subtitle2" color="text.secondary">
                ({getTotalCountForSubGroup() || 0})
              </Typography>
            </Box>
          </Box>
        );
      },
    });

    // Now create columns for each group ID (all status/tag/task-type columns)
    orderedGroupIds.forEach((groupId, index) => {
      const groupInfo = groupingData[groupId];

      baseColumns.push({
        field: `count_${groupId}`,
        headerName: groupInfo.title,
        width: 356, // 340px + 16px gap
        minWidth: 356,
        maxWidth: 356,
        flex: 0,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderHeader: () => (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              px: 1,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                backgroundColor: groupInfo.color || "#4285F2",
                p: 1,
                borderRadius: "28px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                color: "text.secondary",
                height: 48,
                width: 340,
                minWidth: 340,
                maxWidth: 340,
                boxSizing: "border-box",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  justifyContent: "space-between",
                  flexGrow: 1,
                }}
              >
                {groupInfo.icon && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: 1,
                      width: 24,
                      height: 24,
                    }}
                  >
                    <Icon
                      icon={groupInfo.icon}
                      width={24}
                      height={24}
                      style={{
                        color:
                          theme.palette.mode === "dark"
                            ? theme.palette.primary.main
                            : "text.secondary",
                        filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
                      }}
                    />
                  </Box>
                )}

                <Tooltip
                  title={`${groupInfo.description || ""} - ${t(
                    "Grouped by",
                  )} ${groupBy}`}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 500,
                      fontSize: "14px",
                      flexGrow: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: "text.secondary",
                    }}
                  >
                    {groupInfo.title}
                  </Typography>
                </Tooltip>
                <Box
                  sx={{
                    backgroundColor: theme.palette.background.default,
                    borderRadius: "50%",
                    width: 24,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 1,
                    ml: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "8px",
                      fontWeight: 500,
                      color: "text.secondary",
                    }}
                  >
                    {getDisplayCount(groupId)}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        ),
        renderCell: () => {
          return null;
        },
      });
    });

    return baseColumns;
  };

  // Get rows for sub-group DataGrid
  const getSubGroupGridRows = () => {
    const rows = Object.keys(subGroupingData).map((subGroupId, index) => {
      const subGroupInfo = subGroupingData[subGroupId];
      const row: any = {
        id: subGroupId || `subgroup-${index}`,
        title: subGroupInfo.title || `Subgroup ${index + 1}`,
        color: subGroupInfo.color,
        icon: subGroupInfo.icon,
        type: subGroupInfo.type,
      };

      // Add count data for each main group
      Object.keys(groupingData).forEach((groupId) => {
        row[`count_${groupId}`] =
          subGroupData[subGroupId]?.columns?.[groupId]?.tasks?.length || 0;
      });

      return row;
    });

    // if (subGroupBy !== "Assignee") {
    return rows;
    // }
    // Filter out rows where all counts are 0
    // const filteredRows = rows.filter((row) => {
    //   const countKeys = Object.keys(groupingData).map(
    //     (groupId) => `count_${groupId}`
    //   );
    //   const hasNonZeroCount = countKeys.some(
    //     (countKey) => (row[countKey] || 0) > 0
    //   );
    //   return hasNonZeroCount;
    // });

    // return filteredRows;
  };

  // Get loading status
  const getLoadingStatus = () => {
    const keys = Object.keys(loadingStatuses);
    let isLoading = false;
    keys?.forEach((key) => {
      if (
        loadingStatuses?.[key]?.isInitialLoading ||
        loadingStatuses?.[key]?.isLoadingMore
      ) {
        isLoading = true;
      }
    });
    return isLoading;
  };

  // Render detail panel content for sub-group rows
  const renderDetailPanelContent = (params: GridRowParams) => {
    const subGroupId = params.row.id;

    // Use the ordered column order from groupingData
    const orderedColumnIds = Object.keys(groupingData).filter(
      (id) => groupingData[id] && !groupingData[id].hidden,
    );

    return (
      <Box
        sx={{
          py: 2,
          pl: "386px", // Match first column width (340px) + expander column space
          pr: 2,
        }}
      >
        <DragDropContext
          onDragEnd={handleDragEnd}
          onDragStart={handleDragStart}
        >
          <AutoScrollContainer
            style={{
              display: "flex",
              minHeight: "300px",
              paddingBottom: "16px",
              gap: "16px",
              overflowX: "auto",
            }}
          >
            {orderedColumnIds.map((columnId) => {
              const groupInfo = groupingData[columnId];
              const subGroupColumns = subGroupData[subGroupId]?.columns || {};
              const column = subGroupColumns[columnId];

              // Check if sub-group data exists and if this specific column data exists
              const hasSubGroupData = subGroupData && subGroupData[subGroupId];
              const hasColumnData = hasSubGroupData && column;

              // Determine loading state
              const subGroupLoadingState = hasSubGroupData
                ? subGroupData[subGroupId]?.loadingStatuses?.[columnId] || {
                    isInitialLoading: false,
                    isLoadingMore: false,
                  }
                : {
                    isInitialLoading: true,
                    isLoadingMore: false,
                  };

              // Create a default column structure for skeleton loading
              const columnForDisplay = hasColumnData
                ? {
                    ...column,
                    // Use composite ID for subgroup: "subGroupId-columnId"
                    id: `${subGroupId}-${columnId}`,
                  }
                : {
                    id: `${subGroupId}-${columnId}`, // Composite ID for subgroup
                    title: groupInfo.title,
                    color: groupInfo.color,
                    icon: groupInfo.icon,
                    tasks: [],
                    totalCount: 0,
                    description: groupInfo.description || "",
                    hidden: groupInfo.hidden || false,
                  };

              if (groupInfo.hidden) {
                return null;
              }

              return (
                <div
                  key={`${subGroupId}-${columnId}`}
                  style={{
                    width: "340px",
                    minWidth: "340px",
                    flexShrink: 0,
                  }}
                  data-column-id={`${subGroupId}-${columnId}`}
                >
                  <Column
                    column={columnForDisplay}
                    tasks={hasColumnData ? column.tasks || [] : []}
                    onTitleChange={() => {}}
                    onDelete={() => {}}
                    dragHandleProps={undefined}
                    isInitialLoading={subGroupLoadingState.isInitialLoading}
                    isLoadingMore={subGroupLoadingState.isLoadingMore}
                    refreshData={() => refreshColumnData(columnId, subGroupId)}
                    onLoadMore={() => handleLoadMore(columnId, subGroupId)}
                    hasMore={
                      hasSubGroupData
                        ? subGroupData[subGroupId]?.hasMore?.[columnId] || false
                        : false
                    }
                    onTaskUpdate={handleTaskUpdate}
                    canEditColumn={false}
                    hideHeader={true} // Hide the header in detail panel
                  />
                </div>
              );
            })}
          </AutoScrollContainer>
        </DragDropContext>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "calc(100vh - 320px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: 1,
          width: "100%",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            flex: 1,
            width: "100%",
            overflow: "auto",
            maxHeight: "100%",
          }}
        >
          <DataGridPro
            rows={getSubGroupGridRows()}
            columns={getSubGroupGridColumns()}
            getDetailPanelContent={renderDetailPanelContent}
            loading={getLoadingStatus()}
            getDetailPanelHeight={() => "auto"}
            getRowId={(row) =>
              row.id || row.title || Math.random().toString(36).substr(2, 9)
            }
            disableColumnReorder
            slots={{
              detailPanelExpandIcon: KeyboardArrowRight,
              detailPanelCollapseIcon: KeyboardArrowDown,
            }}
            hideFooter
            disableColumnResize={true}
            disableRowSelectionOnClick
            style={{
              height: "100%",
              width: "100%",
              minWidth: "fit-content",
              border: "none",
            }}
            sx={{
              "& .MuiDataGrid-main": {
                overflow: "auto",
                maxHeight: "100%",
              },
              "& .MuiDataGrid-virtualScroller": {
                overflow: "auto",
                maxHeight: "100%",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "transparent",
                borderRadius: 0,
                padding: 1,
                marginBottom: 1,
                minHeight: "64px !important",
                "& .MuiDataGrid-columnHeader": {
                  padding: 0,
                  "&:focus": {
                    outline: "none",
                  },
                  "&:focus-within": {
                    outline: "none",
                  },
                },
              },
              "& .MuiDataGrid-columnSeparator": {
                display: "none",
              },
              "& .MuiDataGrid-cell": {
                border: "none",
                "&:focus": {
                  outline: "none",
                },
                "&:focus-within": {
                  outline: "none",
                },
              },
              "& .MuiDataGrid-row": {
                "&:hover": {
                  backgroundColor: "transparent",
                },
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SubGroupDataGrid;
